import {
  OrderGetResponseType,
  SupplierRelationshipEnumType,
  SupplierRelationshipPaymentTermsType,
} from "@sellar-io/api-v2/types";
import { getOrderStage, OrderCommentList } from "../../components";
import { OrderContactSummary } from "../../components/newOrderSummary/Summary/OrderContactSummary";
import { OrderDeliverySummary } from "../../components/newOrderSummary/Summary/OrderDeliverySummary";
import { OrderDeliveryEvidenceSummary } from "../../components/newOrderSummary/Summary/OrderDeliveryEvidenceSummary";
import { OrderInvoiceSummary } from "../../components/newOrderSummary/Summary/OrderInvoiceSummary";
import { OrderPaymentSummary } from "../../components/newOrderSummary/Summary/OrderPaymentSummary";
import {
  getInvoiceEmails,
  RetailerBusinessEmailsType,
} from "../../utils/sellarHelpers";
import { OrderDocumentSummary } from "../../components/newOrderSummary/Summary/OrderDocumentSummary";
import { useOrderDistributionDeliveryArea } from "../../hooks/api/v2/deliveryArea/useOrderDistributionDeliveryArea";
import { useDistributionDeliveryEstimate } from "~/hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";

export const SharedOrderCards = ({
  order,
  connection,
  buyer,
  showComments = true,
  readOnly = false,
}: {
  order: OrderGetResponseType;
  connection?: {
    connectionType: SupplierRelationshipEnumType;
    buyerInvoiceEmails?: string[] | null;
    paymentTerms: SupplierRelationshipPaymentTermsType | null;
  };
  buyer?: RetailerBusinessEmailsType;
  showComments?: boolean;
  readOnly?: boolean;
}) => {
  const { usesPalletInbound: supplierUsesPalletInboundForOrder } =
    useOrderDistributionDeliveryArea({
      fulfillmentType: order.fulfillmentType,
      supplierId: order.SupplierId,
      postcode: order.Retailer.Business.DeliveryAddress.postalCode,
    });

  const deliveryDate =
    order.deliveredAt ?? order.deliveryPlannedAt ?? undefined;
  const dispatchDate =
    order.dispatchedAt ?? order.dispatchPlannedAt ?? undefined;

  const orderStage = () => getOrderStage(order);

  const invoiceEmails = getInvoiceEmails({ order, connection, buyer });

  const { orderByDate: supplierCutOffDate } = useDistributionDeliveryEstimate({
    supplierId: order?.Supplier.id ?? 0,
    productIds: order?.OrderItems.map((item) => item.ProductId) ?? [],
    buyerPostcode: order?.Retailer.Business.DeliveryAddress.postalCode ?? "",
    cutoffPolicy: "supplierOrderFinalisation",
  });

  return (
    <>
      <OrderDeliverySummary
        fulfillmentType={order.fulfillmentType}
        DeliveryAddress={order.DeliveryAddress}
        stage={orderStage()}
        deliveryDate={deliveryDate}
        dispatchDate={dispatchDate}
        distributionTimePreference={
          order.OrderSellarDistributionMeta?.timePreference
        }
        supplierName={order.Supplier.Business.name}
        deliveryNotes={order.deliveryNotes}
        buyerName={order.Retailer.Business.name}
        orderId={order.id}
        items={order.OrderItems}
        supplierId={order.SupplierId}
        buyerPostcode={order.Retailer.Business.DeliveryAddress.postalCode}
        confirmedAt={order.confirmedAt}
        cancelledAt={order.cancelledAt}
        drayDeliveryContactDetails={[
          order.OrderSellarDistributionMeta?.name,
          order.OrderSellarDistributionMeta?.phoneNumber,
          order.OrderSellarDistributionMeta?.email,
        ]
          .filter(Boolean)
          .join(", ")}
        paymentType={order.paymentType}
        initiator={order.initiator}
        SupplierPalletShipment={order.SupplierPalletShipment}
        supplierUsesPalletInboundForOrder={supplierUsesPalletInboundForOrder}
        readOnly={readOnly}
        supplierCutOffDate={supplierCutOffDate}
        supplierFinalisationCutOffAt={
          order.OrderSellarDistributionMeta?.supplierFinalisationCutOffAt ??
          null
        }
      />
      <OrderDeliveryEvidenceSummary
        orderId={order.id}
        fulfillmentType={order.fulfillmentType}
      />
      <OrderPaymentSummary
        fulfillmentType={order.fulfillmentType}
        stage={orderStage()}
        paymentStatus={order.paymentStatus}
        paymentType={order.paymentType}
        paymentDue={order.paymentDue}
        paymentTerms={connection?.paymentTerms}
        paidAt={order.paidAt}
        stripeInvoiceId={order.stripeInvoiceId}
        orderId={order.id}
        invoiceFinalizedAt={order.invoiceFinalizedAt}
        cancelledBy={order.cancelledBy ?? ""}
        invoiceCreationStage={order.Supplier.invoiceCreationStage}
        confirmedAt={order.confirmedAt}
        dispatchedAt={order.dispatchedAt}
        deliveredAt={order.deliveredAt}
        orderPrice={order.price}
        paymentList={order.PaymentList}
        supplierStripeAccountId={order.Supplier.stripeAccountId ?? undefined}
        hasPaymentLink={Boolean(order.PaymentLink)}
        paymentLinkCode={order.PaymentLink?.code}
        retailerInvoiceEmails={invoiceEmails}
        supplierName={order.Supplier.Business.name}
      />
      <OrderInvoiceSummary
        orderId={order.id}
        cancelledAt={order.cancelledAt}
        paymentStatus={order.paymentStatus}
        paymentType={order.paymentType}
        InvoiceAddress={order.InvoiceAddress}
        invoiceEmails={invoiceEmails}
        stripeInvoiceId={order.stripeInvoiceId}
        stripeAccountId={order.Supplier.stripeAccountId}
        payment={order.Payment}
      />
      {["sellarCredit", "cardPaymentWithDeposit"].includes(
        order.paymentType ?? "",
      ) && (
        <OrderDocumentSummary
          orderId={order.id}
          isTwoOrder={Boolean(order.TwoOrder?.id)}
        />
      )}
      <OrderContactSummary
        buyerContactSmsNumber={order.retailerContactSmsNumber}
        buyerEmail={order.Retailer.Business.email}
      />
      {showComments && (
        <OrderCommentList orderId={order.id} connection={connection} />
      )}
    </>
  );
};
